import { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditorStore } from '../../stores/editorStore';
import { useWebLlm } from '../WebLlmProvider';
import { useCollaboration } from '../../contexts/CollaborationContext';
const CodeEditor = lazy(() => import('./CodeEditor').then(module => ({ default: module.CodeEditor })));
import { TabBar } from './TabBar';
import { FileExplorer } from './FileExplorer';
import { NewProjectModal } from './NewProjectModal';
import { NewFileModal } from './NewFileModal';
import { AIChatPanel } from '../layout/AIChatPanel';
import { TerminalPanel } from './TerminalPanel';
import { TestingPanel } from './TestingPanel';
import { AutonomousGenerator } from './AutonomousGenerator';
import BeginnerModeEditor from './BeginnerModeEditor';
import VersionDiffViewer from './VersionDiffViewer';
import { ChevronRight, FolderOpen, Plus, MessageSquare, Bot, Upload, FolderUp, FolderX, HardDrive, Trash2, Target, Zap, Maximize2, Minimize2 } from 'lucide-react';
import { updateFile, createFile, createProject, getProjectFiles } from '../../services/editorApi';
import { fileSystemAccess } from '../../services/fileSystem';
import { toast } from 'sonner';
import { Breadcrumb } from '../../mawi-ui';
import { LoadingSpinner, LoadingOverlay } from '../../mawi-ui';
import { Button } from '../../mawi-ui';

import { usePageHeader } from '../../contexts/PageHeaderContext';

interface EditorLayoutProps {

}

export function EditorLayout({}: EditorLayoutProps) {
  const { setLeftContent, clearHeader } = usePageHeader();
  const navigate = useNavigate();
  const { engine, status } = useWebLlm();
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [showTesting, setShowTesting] = useState(false);
  const [showAutonomous, setShowAutonomous] = useState(false);
  const [newFileType, setNewFileType] = useState<'file' | 'folder'>('file');
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isLocalMode, setIsLocalMode] = useState(false);
  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBeginnerMode, setIsBeginnerMode] = useState(false);
  const [showDiffView, setShowDiffView] = useState(false);
  const [diffOldCode, setDiffOldCode] = useState('');
  const [showRightPanel, setShowRightPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  
  const {
    sidebarOpen,
    sidebarWidth,
    setSidebarOpen,
    setSidebarWidth,
    activeTabId,
    files,
    openTabs,
    updateFileContent,
    saveFile,
    currentProject,
    projects,
    setCurrentProject,
    setFiles,
    setProjects,
    addProject,
    openComponentTab,
    showTerminal,
    setShowTerminal
  } = useEditorStore();

  const { state: collabState, joinRoom, leaveRoom, updateCursor } = useCollaboration();

  // Get active file content
  const activeFile = activeTabId ? files.find(f => f.id === activeTabId) : null;

  // Render editor content
  const renderEditorContent = () => {
    // Check for component tabs
    const activeTab = openTabs.find(tab => tab.fileId === activeTabId);
    
    if (activeTab?.isComponent) {
      if (activeTab.componentType === 'testing') {
        return (
          <TestingPanel
            code={activeFile?.content || ''}
            language={activeFile?.language || 'javascript'}
            onTestResults={(results: any) => {
              console.log('Test results:', results);
              toast.success(`Tests completed: ${results.status}`);
            }}
            onDebugSession={(session: any) => {
              console.log('Debug session started:', session);
              toast.info('Debug session active');
            }}
            onQualityReport={(metrics: any) => {
              console.log('Quality report:', metrics);
              toast.success(`Quality score: ${metrics.overall}/100`);
            }}
          />
        );
      }
      
      if (activeTab.componentType === 'autonomous') {
        return (
          <AutonomousGenerator
            onApplicationGenerated={(application: any) => {
              console.log('Application generated:', application);
              toast.success(`Application "${application.name}" generated successfully!`);
            }}
          />
        );
      }
    }

    if (showDiffView && activeFile) {
      return (
        <div className="p-4">
          <VersionDiffViewer
            oldCode={diffOldCode}
            newCode={activeFile.content || ''}
            filename={activeFile.name}
            title="Code Changes"
          />
        </div>
      );
    } else if (isBeginnerMode) {
      return (
        <BeginnerModeEditor
          onSwitchToAdvanced={() => setIsBeginnerMode(false)}
          activeFile={activeFile}
          onFileChange={handleFileChange}
          onSave={handleFileSave}
        />
      );
    } else if (activeFile) {
      return (
        <CodeEditor
          value={activeFile.content || ''}
          language={activeFile.language || 'plaintext'}
          onChange={handleFileChange}
          onSave={handleFileSave}
          webLlmEngine={engine}
          webLlmStatus={status}
        />
      );
    } else if (openTabs.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">No file open</p>
            <p className="text-sm">
              {currentProject
                ? 'Select a file from the explorer to start editing'
                : 'Create or open a project to get started'}
            </p>
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  // Handle resize
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResize = useCallback(() => {
    setIsResizing(false);
  }, []);

  const onResize = useCallback((e: MouseEvent) => {
    if (isResizing) {
      const newWidth = Math.max(200, Math.min(500, e.clientX));
      setSidebarWidth(newWidth);
    }
  }, [isResizing, setSidebarWidth]);

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', onResize);
      window.addEventListener('mouseup', stopResize);
      return () => {
        window.removeEventListener('mousemove', onResize);
        window.removeEventListener('mouseup', stopResize);
      };
    }
  }, [isResizing, onResize, stopResize]);

  const handleFileChange = useCallback((content: string) => {
    if (activeTabId) {
      updateFileContent(activeTabId, content);
    }
  }, [activeTabId, updateFileContent]);

  const handleFileSave = useCallback(async () => {
    if (activeTabId) {
      const file = files.find(f => f.id === activeTabId);
      if (file) {
        console.log('DEBUG: handleFileSave called', {
          activeTabId,
          fileId: file.id,
          isLocalMode,
          startsWithLocal: file.id.startsWith('local-'),
          filePath: file.path,
        });
        try {
          // Check if this is a local file (either in local mode or has local- prefix)
          if (isLocalMode || file.id.startsWith('local-')) {
            console.log('DEBUG: Using local file system save');
            // Save to local file system using File System Access API
            if (fileSystemAccess.getOpenedFolder()) {
              await fileSystemAccess.writeFile(file.path, file.content);
              saveFile(activeTabId);
              toast.success('File saved successfully');
            } else {
              toast.error('No local folder is opened. Please open a folder first.');
            }
          } else {
            console.log('DEBUG: Using backend API save');
            // Save to backend API
            await updateFile(file.id, { content: file.content });
            saveFile(activeTabId);
            toast.success('File saved successfully');
          }
        } catch (error) {
          console.error('Failed to save file:', error);
          toast.error('Failed to save file');
        }
      }
    }
  }, [activeTabId, files, isLocalMode, saveFile]);

  const handleCreateProject = useCallback(() => {
    setShowNewProjectModal(true);
  }, []);

  const handleNewFile = useCallback((isFolder: boolean = false) => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }
    setNewFileType(isFolder ? 'folder' : 'file');
    setShowNewFileModal(true);
  }, [currentProject]);

  const handleImportFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportProject = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  const processFileUpload = useCallback(async (fileList: FileList) => {
    if (!currentProject) {
      toast.error('Please create or select a project first');
      return;
    }

    try {
      const file = fileList[0];
      const content = await file.text();
      const language = file.name.split('.').pop() || 'plaintext';
      
      const fileData = {
        name: file.name,
        path: `/${file.name}`,
        content,
        language,
        file_type: 'file' as const,
      };

      await createFile(currentProject.id, fileData);
      
      // Refresh files
      const files = await getProjectFiles(currentProject.id);
      // Update store files (you may need to add setFiles to store)
      
      toast.success(`File "${file.name}" imported successfully!`);
    } catch (error) {
      console.error('Failed to import file:', error);
      toast.error('Failed to import file');
    }
  }, [currentProject]);

  const processFolderUpload = useCallback(async (fileList: FileList) => {
    if (!currentProject) {
      toast.error('Please create or select a project first');
      return;
    }

    try {
      let importCount = 0;
      for (const file of Array.from(fileList)) {
        if (file.type === '' && !file.name.includes('.')) {
          // Skip directories (they're handled by webkitdirectory)
          continue;
        }
        
        const content = await (file as any).text();
        const language = file.name.split('.').pop() || 'plaintext';
        
        const fileData = {
          name: file.name,
          path: `/${file.name}`,
          content,
          language,
          file_type: 'file' as const,
        };

        await createFile(currentProject.id, fileData);
        importCount++;
      }
      
      toast.success(`Imported ${importCount} file(s) successfully!`);
      
      // Refresh files
      const files = await getProjectFiles(currentProject.id);
    } catch (error) {
      console.error('Failed to import folder:', error);
      toast.error('Failed to import folder');
    }
  }, [currentProject]);

  const handleOpenLocalFolder = useCallback(async () => {
    if (!fileSystemAccess.isSupported()) {
      toast.error('File System Access API is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    setIsLoading(true);
    try {
      const folder = await fileSystemAccess.openFolder();
      if (folder) {
        setIsLocalMode(true);
        
        // Create a virtual project for the local folder
        const localProject = {
          id: `local-${folder.name}`,
          name: folder.name,
          description: `Local folder: ${folder.name}`,
          language: 'mixed',
        };
        
        // Set as current project
        setCurrentProject(localProject);
        
        // Load files from local folder
        const filePaths = fileSystemAccess.listFiles();
        
        // Convert to editor files and update store
        // Note: Store paths WITHOUT leading slash for local files so they match the fileSystemAccess keys
        const editorFiles = filePaths.map((path) => ({
          id: `local-${path}`,
          name: path.split('/').pop() || path,
          path: path,  // Keep original path without leading slash for local files
          content: '',
          language: path.split('.').pop() || 'plaintext',
          isDirty: false,
          projectId: localProject.id,
        }));
        
        // Update the store's files
        setFiles(editorFiles);
        
        toast.success(`Opened folder: ${folder.name} with ${filePaths.length} files`);
      }
    } catch (error) {
      if ((error as any).message !== 'No folder is opened') {
        console.error('Failed to open folder:', error);
        toast.error('Failed to open folder');
      }
    } finally {
      setIsLoading(false);
    }
  }, [setCurrentProject]);

  const handleCloseProject = useCallback(() => {
    if (isLocalMode) {
      fileSystemAccess.closeFolder();
      setIsLocalMode(false);
    }

    // Leave collaboration room
    leaveRoom();

    // Clear current project from store
    setCurrentProject(null);
    toast.success('Project closed');
  }, [isLocalMode, setCurrentProject, leaveRoom]);

  const handleSelectProject = useCallback(async (project: typeof projects[0]) => {
    setIsLoading(true);
    try {
      // Clear local mode and local files when switching to a cloud project
      if (isLocalMode) {
        fileSystemAccess.closeFolder();
        setIsLocalMode(false);
        setFiles([]);
      }
      setCurrentProject(project);

      // Join collaboration room for this project
      joinRoom(`project-${project.id}`, {
        id: `user-${Date.now()}`, // In real app, use actual user ID
        name: 'You', // In real app, use actual user name
        color: '#3B82F6',
      });

      // Load project files from backend
      try {
        const projectFiles = await getProjectFiles(project.id);
        setFiles(projectFiles.map(f => ({
          id: f.id,
          name: f.name,
          path: f.path,
          content: f.content || '',
          language: f.language || 'plaintext',
          isDirty: false,
          projectId: f.project_id,
        })));
      } catch (err) {
        console.error('Failed to load project files:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setCurrentProject, isLocalMode, setFiles, getProjectFiles, joinRoom]);

  const removeProject = useCallback((projectId: string) => {
    // Just remove from the local projects list (doesn't delete from backend)
    const updatedProjects = projects.filter(p => p.id !== projectId);
    setProjects(updatedProjects);
    // If this was the current project, clear it
    if (currentProject?.id === projectId) {
      setCurrentProject(null);
    }
  }, [projects, setProjects, currentProject, setCurrentProject]);

  // Load projects on mount (only if not in local mode)
  // Note: We load the projects list but don't auto-select any project
  useEffect(() => {
    if (!isLocalMode && !hasLoadedProjects) {
      setHasLoadedProjects(true);
      // Don't auto-select a project - let user choose from the list
    }
  }, [isLocalMode, hasLoadedProjects]);

  // Inject editor controls into global header
  useEffect(() => {
    setLeftContent(
      <div className="flex items-center justify-between w-full">
        <Breadcrumb
          items={[
            { label: 'Editor', href: '/editor' },
            ...(currentProject ? [{ label: currentProject.name, onClick: () => navigate(`/editor/${currentProject.id}`) }] : []),
            ...(activeFile ? [{ label: activeFile.name }] : []),
          ]}
        />
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs text-gray-500 dark:text-gray-400">Mode:</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isBeginnerMode}
              onChange={(e) => setIsBeginnerMode(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Beginner</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showDiffView}
              onChange={(e) => {
                if (!showDiffView) {
                  setDiffOldCode(activeFile?.content || '');
                }
                setShowDiffView(e.target.checked);
              }}
              className="w-4 h-4"
            />
            <span className="text-sm">Diff</span>
          </label>
        </div>
      </div>
    );

    return () => clearHeader();
  }, [currentProject, activeFile, isBeginnerMode, showDiffView]);

  return (
    <div className={`flex h-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg`}>
      {/* Sidebar Toggle */}
      {!sidebarOpen && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-100 dark:bg-gray-800 rounded-r-lg shadow"
          onClick={() => setSidebarOpen(true)}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar */}
      {sidebarOpen && (
        <div
          className="flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 relative"
          style={{ width: sidebarWidth }}
        >
          {/* Global Loader */}
          {isLoading && (
            <div className="absolute inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-10">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

            {/* File Explorer View */}
            <>
          {/* Project Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase text-gray-500">
                Project
              </span>
              <div className="flex gap-1 items-center">
                {/* Collapse Sidebar Button */}
                <button
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  onClick={() => setSidebarOpen(false)}
                  title="Collapse Sidebar"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </button>
                <button
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  onClick={() => setShowNewFileModal(true)}
                  title="New File"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  onClick={handleImportFile}
                  title="Import File"
                >
                  <Upload className="w-4 h-4" />
                </button>
                <button
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  onClick={handleCreateProject}
                  title="New Project"
                >
                  <FolderOpen className="w-4 h-4" />
                </button>
                <button
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  onClick={handleOpenLocalFolder}
                  title="Open Local Folder (Direct Access)"
                >
                  <HardDrive className="w-4 h-4" />
                </button>
                {currentProject && (
                  <button
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900 text-red-500 rounded"
                    onClick={handleCloseProject}
                    title="Close Project"
                  >
                    <FolderX className="w-4 h-4" />
                  </button>
                )}
              </div>
              </div>
              {currentProject ? (
                <div className="text-sm font-medium truncate">
                  {currentProject.name}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No project selected</div>
              )}
              </div>

              {/* File Explorer */}
              <div className="flex-1 overflow-hidden">
                <FileExplorer onNewFile={handleNewFile} />
              </div>

              {/* Bottom Toolbar */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">

                {/* Testing & Debugging Button */}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    openComponentTab('testing', 'Testing & Debug');
                    setShowTesting(false);
                  }}
                >
                  <Target className="w-4 h-4 text-green-500" />
                  <span>Testing & Debug</span>
                </button>

                {/* Autonomous Application Generator */}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    openComponentTab('autonomous', 'Application Generator');
                    setShowAutonomous(false);
                  }}
                >
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span>Autonomous App Gen</span>
                </button>
              </div>
            </>

          {/* Resize Handle */}
          <div
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 bg-transparent"
            onMouseDown={startResize}
          />
        </div>
      )}

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col min-w-0">


        {/* Tab Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
          <TabBar />
        </div>

        {/* Main View Panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">

          {/* Editor Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Editor */}
            <div className="flex-1 overflow-hidden relative">
              <LoadingOverlay isVisible={isLoading} text="Loading project..." />
              {renderEditorContent()}
            </div>

            {/* Terminal Panel */}
            <TerminalPanel 
              isOpen={showTerminal} 
              onClose={() => setShowTerminal(false)} 
            />
          </div>

        {/* Right Panel: Chat Panel */}
        {false && (
          <div className="w-80 border-l border-border bg-background">
            {/* RightPanel component not implemented yet */}
          </div>
        )}
       </div>


      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={(e) => {
          if (e.target.files?.length) {
            processFileUpload(e.target.files);
          }
          e.target.value = '';
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        className="hidden"
        // @ts-ignore - webkitdirectory is not in standard types
        webkitdirectory=""
        directory=""
        multiple
        onChange={(e) => {
          if (e.target.files?.length) {
            processFolderUpload(e.target.files);
          }
          e.target.value = '';
        }}
      />

      {/* Modals */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
      />
      <NewFileModal
        isOpen={showNewFileModal}
        onClose={() => setShowNewFileModal(false)}
        isFolder={newFileType === 'folder'}
      />
      </div>
      </div>
    </div>
  );
}